import { useEffect, useMemo, useState } from "react";
import {
    Activity, AlertTriangle, ArrowUpRight, CheckCircle2, Clock, FileWarning,
    Gauge, Loader2, PlayCircle, RefreshCw, ShieldAlert, Timer, TrendingUp,
    Waves, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";

const SEMAFORO = {
    verde: {
        color: "text-[color:var(--brand-exito)]",
        bg: "bg-[color:var(--brand-exito)]/10",
        border: "border-[color:var(--brand-exito)]/40",
        label: "Operación estable",
        Icon: CheckCircle2,
    },
    amarillo: {
        color: "text-[color:var(--brand-dark-gold)]",
        bg: "bg-[color:var(--brand-gold)]/20",
        border: "border-[color:var(--brand-dark-gold)]/40",
        label: "Atención requerida",
        Icon: AlertTriangle,
    },
    rojo: {
        color: "text-[color:var(--brand-coral)]",
        bg: "bg-[color:var(--brand-coral)]/10",
        border: "border-[color:var(--brand-coral)]/40",
        label: "Excepciones críticas",
        Icon: XCircle,
    },
};

const TIPO_ICON = {
    covenant: ShieldAlert,
    obligacion: FileWarning,
    reserva: Waves,
    agent_request: Clock,
};
const SEV_BADGE = {
    alta: "badge-error",
    media: "badge-gold",
    baja: "badge-exito",
};

function fmtDate(iso) {
    if (!iso) return "—";
    return String(iso).slice(0, 16).replace("T", " ");
}

function DailyCloseModal({ tenantId, onDone }) {
    const [open, setOpen] = useState(false);
    const [fecha, setFecha] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    });
    const [motivo, setMotivo] = useState("");
    const [force, setForce] = useState(false);
    const [running, setRunning] = useState(false);

    const submit = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(
                `/tenants/${tenantId}/daily-close/manual`,
                { fecha, force, motivo: motivo || null },
            );
            if (data.skipped) {
                toast.info("Cierre ya ejecutado", {
                    description: "Marca 'Re-ejecutar (force)' si deseas correrlo de nuevo.",
                });
            } else {
                toast.success(`Cierre ${fecha} · ${data.estado_global?.toUpperCase()}`, {
                    description: `${data.pasos?.length || 0} pasos · ${data.errores_totales || 0} errores`,
                });
            }
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error("Error ejecutando cierre", {
                description: err?.response?.data?.detail || "Verifica permisos",
            });
        } finally {
            setRunning(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="daily-close-open-btn">
                    <PlayCircle className="h-4 w-4 mr-2" /> Ejecutar cierre manual
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Ejecutar daily close manual</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        Ejecuta los 8 pasos secuenciales: intereses · ingesta A2 · BB · covenants ·
                        reservas · conciliación · snapshots · reporte excepciones.
                        Todo idempotente por fecha. Registra en bitácora con tu usuario.
                    </p>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Fecha objetivo
                        </label>
                        <Input type="date" value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                            className="mt-1"
                            data-testid="daily-close-fecha-input" />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Motivo (opcional)
                        </label>
                        <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ej. reproceso post-fix conciliación"
                            className="mt-1"
                            data-testid="daily-close-motivo-input" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={force}
                            onChange={(e) => setForce(e.target.checked)}
                            data-testid="daily-close-force-check" />
                        Re-ejecutar aunque ya haya corrido (force)
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={running}>
                        Cancelar
                    </Button>
                    <Button onClick={submit} disabled={running}
                        data-testid="daily-close-submit-btn">
                        {running ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Ejecutando…</>)
                                : (<><PlayCircle className="h-4 w-4 mr-2" /> Ejecutar ahora</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function OperacionDiaria() {
    const { hasRole } = useAuth();
    const { activeTenantId, tenant } = useTenant();
    const [radar, setRadar] = useState(null);
    const [exceptions, setExceptions] = useState({ items: [], total: 0 });
    const [historico, setHistorico] = useState([]);
    const [loading, setLoading] = useState(true);

    const canRunClose = useMemo(() => hasRole([
        "administrador_fiduciario", "tesoreria", "operaciones",
        "admin_ti", "direccion",
    ]), [hasRole]);

    const load = async () => {
        if (!activeTenantId) return;
        setLoading(true);
        try {
            const [r1, r2, r3] = await Promise.all([
                api.get(`/tenants/${activeTenantId}/operacion-diaria/radar`),
                api.get(`/tenants/${activeTenantId}/operacion-diaria/exceptions`),
                api.get(`/tenants/${activeTenantId}/daily-close/historico?limit=30`),
            ]);
            setRadar(r1.data);
            setExceptions(r2.data);
            setHistorico(r3.data);
        } catch (err) {
            toast.error("Error cargando operación diaria", {
                description: err?.response?.data?.detail || "Verifica permisos",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenantId]);

    if (loading || !radar) {
        return (
            <div className="p-10 flex items-center gap-3 text-muted-foreground"
                data-testid="op-diaria-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando radar del día…
            </div>
        );
    }

    const sem = SEMAFORO[radar.estado_global] || SEMAFORO.verde;
    const SemIcon = sem.Icon;
    const dc = radar.ultimo_daily_close;
    const slaPct = Math.round((radar.sla_agent_requests?.ratio_cumplimiento || 0) * 100);

    return (
        <div className="p-8 lg:p-10 space-y-8 max-w-[1600px]" data-testid="operacion-diaria-page">
            {/* Header + acción principal */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Fase 9 · A4 Operación diaria
                    </div>
                    <h1 className="font-display text-4xl tracking-tight">
                        Radar del día
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Estado consolidado del tenant {tenant?.nombre_comercial || tenant?.slug || "activo"}.
                        El cierre diario automatizado corre a las 23:00 UTC. También puedes
                        disparar manualmente por fecha.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load} data-testid="op-diaria-refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
                    </Button>
                    {canRunClose && (
                        <DailyCloseModal tenantId={activeTenantId} onDone={load} />
                    )}
                </div>
            </div>

            {/* Semáforo global */}
            <div className={`kpi-card border-l-4 ${sem.border} ${sem.bg}`}
                data-testid="op-diaria-semaforo">
                <div className="flex items-center gap-4">
                    <SemIcon className={`h-10 w-10 ${sem.color}`} />
                    <div className="flex-1">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Estado global
                        </div>
                        <div className={`font-display text-3xl mt-1 ${sem.color}`}>
                            {sem.label}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Último cierre
                        </div>
                        <div className="font-medium text-sm mt-1 tabular">
                            {dc ? dc.fecha : "Sin corridas"}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            {dc ? `estado: ${dc.estado_global} · ${dc.errores_totales || 0} errores` : ""}
                        </div>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid md:grid-cols-4 gap-4">
                <div className="kpi-card" data-testid="kpi-facilities">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <TrendingUp className="h-3.5 w-3.5" /> Facilities dispuestas
                    </div>
                    <div className="kpi-value mt-2">{radar.facilities_dispuestas}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        con devengo diario activo
                    </div>
                </div>
                <div className="kpi-card" data-testid="kpi-snapshots">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Activity className="h-3.5 w-3.5" /> Snapshots
                    </div>
                    <div className="kpi-value mt-2">
                        {radar.snapshots_hoy}<span className="text-lg text-muted-foreground"> hoy</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {radar.snapshots_ayer} ayer · SHA-256 inmutable
                    </div>
                </div>
                <div className="kpi-card" data-testid="kpi-excepciones">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <AlertTriangle className="h-3.5 w-3.5" /> Excepciones abiertas
                    </div>
                    <div className={`kpi-value mt-2 ${exceptions.total > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                        {exceptions.total}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        breaches · obligaciones · SLAs
                    </div>
                </div>
                <div className="kpi-card" data-testid="kpi-sla">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Gauge className="h-3.5 w-3.5" /> SLA lender 48h
                    </div>
                    <div className={`kpi-value mt-2 ${slaPct >= 90 ? "text-[color:var(--brand-exito)]" : slaPct >= 70 ? "text-[color:var(--brand-dark-gold)]" : "text-[color:var(--brand-coral)]"}`}>
                        {slaPct}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {radar.sla_agent_requests?.respondidos || 0} respondidos ·{" "}
                        <span className="text-[color:var(--brand-coral)]">
                            {radar.sla_agent_requests?.vencidos || 0} vencidos
                        </span>
                    </div>
                </div>
            </div>

            {/* Excepciones + Últimos cierres */}
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 rounded-md border border-border bg-card overflow-hidden"
                    data-testid="exceptions-table">
                    <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <h2 className="font-display text-lg">Excepciones abiertas</h2>
                            <p className="text-[11px] text-muted-foreground">
                                Cross-módulo · ordenadas por severidad
                            </p>
                        </div>
                        <span className="badge-gold">{exceptions.total} activas</span>
                    </div>
                    <table className="trf-table">
                        <thead><tr>
                            <th>Tipo</th>
                            <th>Título</th>
                            <th>Detectada</th>
                            <th>Severidad</th>
                        </tr></thead>
                        <tbody>
                            {exceptions.items
                                .slice()
                                .sort((a, b) => (a.severidad === "alta" ? -1 : 1))
                                .slice(0, 40)
                                .map((e) => {
                                    const Icon = TIPO_ICON[e.tipo] || AlertTriangle;
                                    return (
                                        <tr key={e.id} data-testid={`exception-row-${e.id}`}>
                                            <td>
                                                <div className="flex items-center gap-1.5">
                                                    <Icon className="h-3.5 w-3.5 text-[color:var(--brand-dark-gold)]" />
                                                    <span className="text-xs capitalize">{e.tipo.replace("_", " ")}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="text-sm">{e.titulo}</div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    {e.detalle}
                                                </div>
                                            </td>
                                            <td className="text-[11px] tabular text-muted-foreground">
                                                {fmtDate(e.fecha_deteccion)}
                                            </td>
                                            <td>
                                                <span className={SEV_BADGE[e.severidad] || "badge-gold"}>
                                                    {e.severidad}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            {exceptions.items.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-10 text-muted-foreground">
                                        Sin excepciones abiertas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Últimos cierres timeline */}
                <div className="rounded-md border border-border bg-card overflow-hidden"
                    data-testid="daily-close-history">
                    <div className="px-5 py-3 border-b border-border">
                        <h2 className="font-display text-lg flex items-center gap-2">
                            <Timer className="h-4 w-4 text-[color:var(--brand-dark-gold)]" />
                            Historial de cierres
                        </h2>
                        <p className="text-[11px] text-muted-foreground">
                            Últimos 30 daily closes
                        </p>
                    </div>
                    <div className="max-h-[560px] overflow-y-auto">
                        {historico.length === 0 && (
                            <div className="text-sm text-muted-foreground py-8 text-center">
                                Sin corridas históricas.
                            </div>
                        )}
                        {historico.map((h) => {
                            const s = SEMAFORO[h.estado_global] || SEMAFORO.verde;
                            const SIcon = s.Icon;
                            return (
                                <div key={h.id}
                                    className="flex items-start gap-3 px-4 py-3 border-b border-border/40 hover:bg-muted/40"
                                    data-testid={`dc-history-${h.fecha}`}>
                                    <SIcon className={`h-4 w-4 mt-0.5 ${s.color}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium tabular">{h.fecha}</div>
                                        <div className="text-[10px] text-muted-foreground truncate">
                                            {h.triggered_by} ·{" "}
                                            {h.pasos?.filter?.((p) => p.estado === "ok").length || 0}/
                                            {h.pasos?.length || 0} pasos ok
                                        </div>
                                        {h.motivo && (
                                            <div className="text-[10px] text-muted-foreground truncate italic">
                                                “{h.motivo}”
                                            </div>
                                        )}
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-medium uppercase ${s.color} ${s.border} ${s.bg}`}>
                                        {h.estado_global}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Cross-links a operaciones granulares */}
            <div className="grid md:grid-cols-3 gap-4">
                <a href="/bitacora" className="kpi-card hover:border-[color:var(--brand-dark-gold)] transition"
                    data-testid="cross-link-bitacora">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Verificable
                    </div>
                    <div className="font-display text-lg mt-1">Bitácora inmutable</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        Ir a la cadena SHA-256 <ArrowUpRight className="h-3 w-3" />
                    </div>
                </a>
                <a href="/obligaciones" className="kpi-card hover:border-[color:var(--brand-dark-gold)] transition"
                    data-testid="cross-link-obligaciones">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Alertas
                    </div>
                    <div className="font-display text-lg mt-1">Obligaciones y alertas</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        {radar.excepciones?.obligaciones_vencidas || 0} vencidas
                        <ArrowUpRight className="h-3 w-3" />
                    </div>
                </a>
                <a href="/v1/covenants" className="kpi-card hover:border-[color:var(--brand-dark-gold)] transition"
                    data-testid="cross-link-covenants">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Riesgos
                    </div>
                    <div className="font-display text-lg mt-1">Covenants</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        {radar.excepciones?.breaches_covenants || 0} en breach/cure period
                        <ArrowUpRight className="h-3 w-3" />
                    </div>
                </a>
            </div>
        </div>
    );
}
